import { useEffect, useLayoutEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';

export type OwnerAddOnsAnswersType = {
  email: boolean;
  blacklist: boolean;
  dnc: boolean;
};

export type OwnerAddOnsStepIdType = 'email' | 'dnc' | 'litigator';

type PropsType = {
  onComplete: (answers: OwnerAddOnsAnswersType) => void;
  canAskScrubOptions?: boolean;
  onStepChange?: (stepId: OwnerAddOnsStepIdType | null) => void;
};

const STEPS: { id: OwnerAddOnsStepIdType; text: string }[] = [
  {
    id: 'email',
    text: 'Do you want emails as well?',
  },
  {
    id: 'dnc',
    text: 'Do you want to scrub out all DNC numbers?',
  },
  {
    id: 'litigator',
    text: 'Do you want to scrub all known litigators?',
  },
];

const OwnerAddOnsWizard = ({
  onComplete,
  canAskScrubOptions = true,
  onStepChange,
}: PropsType) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState<OwnerAddOnsAnswersType>({
    email: false,
    blacklist: false,
    dnc: false,
  });

  const currentStep = STEPS[stepIndex];

  useLayoutEffect(() => {
    onStepChange?.(currentStep?.id ?? null);
  }, [currentStep?.id, onStepChange]);

  useEffect(() => {
    return () => onStepChange?.(null);
  }, [onStepChange]);

  const finish = (nextAnswers: OwnerAddOnsAnswersType) => {
    onComplete({
      ...nextAnswers,
      dnc: nextAnswers.blacklist ? nextAnswers.dnc : false,
    });
  };

  const handleSelect = (value: boolean) => {
    if (!currentStep) {
      return;
    }

    const nextAnswers = { ...answers };

    if (currentStep.id === 'email') {
      nextAnswers.email = value;

      if (!canAskScrubOptions) {
        finish({ ...nextAnswers, blacklist: false, dnc: false });
        return;
      }

      setAnswers(nextAnswers);
      setStepIndex(1);
      return;
    }

    if (currentStep.id === 'dnc') {
      nextAnswers.dnc = value;

      if (value) {
        finish({ ...nextAnswers, blacklist: true, dnc: true });
        return;
      }

      setAnswers(nextAnswers);
      setStepIndex(2);
      return;
    }

    nextAnswers.blacklist = value;
    finish(nextAnswers);
  };

  if (!currentStep) {
    return null;
  }

  return (
    <Box
      className="styled-drag-and-drop-area__step-enter"
      sx={{
        width: '100%',
        py: 5,
        px: 2,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        minHeight: 280,
      }}
    >
      <Typography variant="h5" fontWeight={600} textAlign="center" maxWidth={520}>
        {currentStep.text}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          width: '100%',
          maxWidth: 420,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => handleSelect(true)}
          sx={{
            flex: 1,
            py: 2,
            px: 3,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1.1rem',
            fontWeight: 600,
          }}
        >
          Yes
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleSelect(false)}
          sx={{
            flex: 1,
            py: 2,
            px: 3,
            borderRadius: 2,
            textTransform: 'none',
            fontSize: '1.1rem',
            fontWeight: 600,
          }}
        >
          No
        </Button>
      </Box>
    </Box>
  );
};

export default OwnerAddOnsWizard;
